import { Pipe, PipeTransform } from '@angular/core';

/**
 * Pipe that truncates long filenames in the middle while preserving the start and end (including file extension).
 *
 * @example
 * // Basic usage with defaults (maxLength: 40, startChars: 15, endChars: 15)
 * {{ 'very-long-filename-with-many-characters.pdf' | truncateFilename }}
 * // Output: "very-long-file...cters.pdf"
 *
 * @example
 * // Custom parameters
 * {{ filename | truncateFilename: 50 : 20 : 20 }}
 * // Keeps first 20 and last 20 characters, max total 50
 *
 * @example
 * // Mobile friendly (shorter)
 * {{ filename | truncateFilename: 30 : 10 : 10 }}
 * // Keeps first 10 and last 10 characters, max total 30
 */
@Pipe({
  name: 'truncateFilename',
  standalone: true,
})
export class TruncateFilenamePipe implements PipeTransform {
  /**
   * Truncates a filename in the middle, preserving the start and end (including extension)
   * @param filename The full filename to truncate
   * @param maxLength The maximum total length (default: 40)
   * @param startChars Number of characters to keep at the start (default: 15)
   * @param endChars Number of characters to keep at the end (default: 15)
   * @returns The truncated filename with ... in the middle
   */
  transform(
    filename: string | null | undefined,
    maxLength: number = 40,
    startChars: number = 15,
    endChars: number = 15,
  ): string {
    if (!filename) {
      return '';
    }

    // If filename is shorter than max length, return as is
    if (filename.length <= maxLength) {
      return filename;
    }

    // Ensure we have enough space for the ellipsis
    const ellipsis = '...';
    const minLength = startChars + endChars + ellipsis.length;

    // If maxLength is too small, adjust it
    if (maxLength < minLength) {
      maxLength = minLength;
    }

    // Find the last dot to identify the real extension
    const lastDotIndex = filename.lastIndexOf('.');

    // If there's an extension (dot is not at start and not at end)
    if (lastDotIndex > 0 && lastDotIndex < filename.length - 1) {
      const extension = filename.substring(lastDotIndex); // includes the dot
      const nameWithoutExt = filename.substring(0, lastDotIndex);

      // Calculate how many chars we can use for the name part
      const availableForName = maxLength - extension.length - ellipsis.length;

      if (availableForName < startChars + 1) {
        // Not enough space, fallback to simple truncation
        const start = filename.substring(0, startChars);
        const end = filename.substring(filename.length - endChars);
        return `${start}${ellipsis}${end}`;
      }

      // Split available space between start and end of name (without extension)
      const startFromName = Math.floor(
        availableForName * (startChars / (startChars + endChars)),
      );
      const endFromName = availableForName - startFromName;

      const start = nameWithoutExt.substring(0, startFromName);
      const end = nameWithoutExt.substring(nameWithoutExt.length - endFromName);

      return `${start}${ellipsis}${end}${extension}`;
    }

    // No extension found, use original logic
    let start = filename.substring(0, startChars);
    const end = filename.substring(filename.length - endChars);

    // If the end portion starts with a dot, remove one character from start
    // to avoid double dots like "...siamail"
    if (end.startsWith('.')) {
      start = start.substring(0, start.length - 1);
    }

    return `${start}${ellipsis}${end}`;
  }
}
